import { Component, ReactNode } from "react";

import * as Sentry from "@sentry/react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: {
    component: string;
    userId?: string;
    additionalData?: Record<string, any>;
  };
  onReset?: () => void;
}

interface State {
  hasError: boolean;
}

/**
 * Sentry Error Boundary that catches component errors and reports them to Sentry
 * with contextual information for better debugging.
 */
class SentryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { context } = this.props;

    // Set context for Sentry
    if (context) {
      Sentry.setContext("errorBoundary", {
        component: context.component,
        ...context.additionalData,
      });

      if (context.userId) {
        Sentry.setUser({ id: context.userId });
      }
    }

    // Capture the error with component stack
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        error_boundary: context?.component || "unknown",
      },
    });

    console.error("Error boundary caught error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Card className="border-destructive">
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <div>
                  <CardTitle>Something Went Wrong</CardTitle>
                  <CardDescription>
                    An unexpected error occurred. Our team has been notified.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  We're sorry for the inconvenience. You can try again or return to the dashboard.
                </p>
                <p className="text-xs text-muted-foreground">
                  This error has been automatically reported to help us improve the experience.
                </p>
              </div>

              <div className="flex gap-2">
                <Button onClick={this.handleReset} variant="outline">
                  Try Again
                </Button>
                <Button onClick={this.handleGoHome}>
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SentryErrorBoundary;
